import Link from 'next/link';
import type { Project } from '@/lib/projects';
import { BrowserMockup } from '@/components/ui/BrowserMockup';
import { PhoneMockup } from '@/components/ui/PhoneMockup';
import { StatusBadge } from './StatusBadge';
import { StackTags } from './StackTags';

type Props = {
  project: Project;
  index: number;
};

function ExternalArrow() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 17 17 7" />
      <polyline points="8 7 17 7 17 16" />
    </svg>
  );
}

export function FeaturedProjectCard({ project, index }: Props) {
  const reversed = index % 2 === 1;
  const Mockup = project.mockup === 'phone' ? PhoneMockup : BrowserMockup;

  return (
    <article
      className="featured-card group relative w-full transition-all duration-[400ms] ease-out"
      style={
        {
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderLeft: `3px solid ${project.accentColor}`,
          borderRadius: 'var(--radius-2xl)',
          padding: 'clamp(24px, 4vw, 40px)',
          ['--project-accent' as string]: project.accentColor,
        } as React.CSSProperties
      }
    >
      <div
        className="grid items-center gap-8 md:gap-10"
        style={{
          gridTemplateColumns: '1fr',
        }}
      >
        <div
          className="md:grid md:gap-10 md:items-center"
          style={{
            gridTemplateColumns: '1fr 1fr',
          }}
        >
          <div className={`order-1 ${reversed ? 'md:order-2' : 'md:order-1'} flex justify-center`}>
            <div className="w-full max-w-[520px]">
              <Mockup project={project} />
            </div>
          </div>

          <div
            className={`order-2 ${reversed ? 'md:order-1' : 'md:order-2'} mt-8 md:mt-0 flex flex-col gap-5`}
          >
            <StatusBadge project={project} />

            <div>
              <h3
                className="font-display italic text-text"
                style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 }}
              >
                {project.name}
              </h3>
              <p
                className="mt-1 italic text-text-secondary"
                style={{ fontSize: 14 }}
              >
                {project.tagline}
              </p>
            </div>

            <p
              className="text-text-secondary"
              style={{
                fontSize: 14,
                lineHeight: 1.75,
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {project.description}
            </p>

            <StackTags stack={project.stack} limit={6} />

            <div className="flex flex-wrap items-center gap-5">
              <Link
                href={`/projects/${project.slug}`}
                className="case-link inline-flex items-center gap-1.5 font-sans"
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--accent)',
                }}
              >
                Case study
                <span className="case-arrow transition-transform duration-300">→</span>
              </Link>
              {project.liveUrl && (
                <a
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit ${project.name} live site (opens in new tab)`}
                  className="visit-site-link inline-flex items-center gap-1 font-mono transition-colors"
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                  }}
                >
                  Visit Site
                  <ExternalArrow />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
